import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { marginSizes } from 'theme/default'
import breakpoint from 'tools/styled-components-breakpoint'
import Component from 'decorators/Component'
import { formatDiff } from 'tools/number'
import { H4 } from 'components/Typo'
import Flex from 'components/Flex'
import Form from 'components/Form'
import { Header, ContentContainer, ItemContainer } from './ContentVariantElements'

const RadioContainer = styled(ItemContainer)`
  align-items: flex-start;

  &:hover {
    cursor: pointer;
  }
`

const NameContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'center'
}))`
  flex: 0 0 70%;
  ${breakpoint('tablet')`
    flex: 0 0 80%;
  `}
`

const AmountContainer = styled(Flex)`
  flex: 0 0 30%;
  ${breakpoint('tablet')`
    flex: 0 0 20%;
  `}
`

const Amount = styled(H4).attrs(() => ({
  marginTop: marginSizes.small,
  marginBottom: marginSizes.small,
  textAlign: 'right'
}))`
  /* work around to handle edge cases,
  until we have a new ProductOptionModal design */
  word-break: break-all;
`

const StyledHeader = styled(Header)`
  min-width: 175px;
`

export const VariantHeader = Component()(({
  t,
  show = true,
  selectedVariant = {},
  selectedVarPriceDiff = 0,
  formatMoney = x => x.toString(),
  hideArrow = false,
  variantPriceDisplay = 'priceDifference'
}) => {
  const getVariantPrice = ({ selectedVariant }) => {
    if (variantPriceDisplay === 'priceDifference') {
      return selectedVarPriceDiff <= 0 ? '' : formatDiff({ value: selectedVarPriceDiff, formatter: formatMoney })
    } else {
      return formatMoney(selectedVariant.price)
    }
  }

  return (
    <StyledHeader
      show={show}
      title={t('Choose from below')}
      subtitle={t('Select 1')}
      hideArrow={hideArrow}
      body={selectedVariant.variantName || ''}
      bodySubText={getVariantPrice({ selectedVariant })} />
  )
})

VariantHeader.propTypes = {
  show: PropTypes.bool,
  selectedVariant: PropTypes.object,
  selectedVarPriceDiff: PropTypes.number,
  formatMoney: PropTypes.func,
  hideArrow: PropTypes.bool,
  variantPriceDisplay: PropTypes.string
}

export const VariantContent = ({
  show = true,
  selectedVariant = {},
  variants = [],
  formatMoney = x => x.toString(),
  calculateVariantDiff = x => 0,
  variantPriceDisplay = 'priceDifference'
}) => {
  const getVariantOptionPrice = ({ variant }) => {
    if (variantPriceDisplay === 'priceDifference') {
      const variantDiff = calculateVariantDiff(variant.id)
      return variantDiff <= 0 ? '' : formatDiff({ value: variantDiff, formatter: formatMoney })
    } else {
      return formatMoney(variant.price)
    }
  }

  return (
    <ContentContainer show={show}>
      <Form.RadioGroup name='selectedVariant' selected={variants.length > 0 && variants[0].id}>
        {(variants || []).map((variant, idx) => (
          <Form.Radio
            key={idx}
            value={variant.id}
            Container={(props) => (
              <RadioContainer key={variant.id} last={idx === variants.length - 1} {...props} />
            )}>
            {({ checked }) => (
              <React.Fragment>
                <NameContainer>
                  <Form.Radio.Dot checked={checked} />
                  <H4 margin='small'>{variant.variantName}</H4>
                </NameContainer>
                <AmountContainer>
                  <Amount data-testid={`text.amount.${idx}`}>
                    {getVariantOptionPrice({ variant })}
                  </Amount>
                </AmountContainer>
              </React.Fragment>
            )}
          </Form.Radio>
        ))}
      </Form.RadioGroup>
    </ContentContainer>
  )
}

VariantContent.propTypes = {
  show: PropTypes.bool,
  selectedVariant: PropTypes.object,
  variants: PropTypes.array,
  formatMoney: PropTypes.func,
  calculateVariantDiff: PropTypes.func,
  variantPriceDisplay: PropTypes.string
}
